import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  FolderPlus, 
  Loader2,
  ArrowLeft,
  MapPin,
  User,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

export function PortalNewProject() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    address: '',
    postcode: '',
    description: '',
    notes: ''
  });

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      // Fetch only Contractor category contacts
      const response = await api.get('/contacts?category=Contractor');
      setContractors(response.data || []);
    } catch (error) {
      console.error('Failed to fetch contractors:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.client_id && !formData.client_name.trim()) {
      toast.error('Please select a contractor');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Please enter the site address');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/portal/submit-project', {
        project_type: 'CON', // Always LUX Construction
        client_id: formData.client_id || null,
        client_name: formData.client_name,
        address: formData.address,
        postcode: formData.postcode,
        status: 'Live', // Default status
        description: formData.description,
        notes: formData.notes
      });
      
      toast.success(`Project ${response.data.project_ref} created successfully!`);
      navigate('/portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const handleContractorSelect = (contractorId) => {
    const contractor = contractors.find(c => c.id === contractorId);
    setFormData(prev => ({
      ...prev,
      client_id: contractorId,
      client_name: contractor?.name || ''
    }));
  };

  return (
    <div className="p-4 space-y-5 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/portal')}
          className="h-10 w-10 p-0 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-white">New Project</h1>
          <p className="text-sm text-gray-400">LUX Construction</p>
        </div>
      </div>

      {/* Form */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <FolderPlus className="w-5 h-5 text-green-400" />
            Project Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Contractor Selection */}
          <div className="space-y-2">
            <Label className="text-gray-300 text-base flex items-center gap-2">
              <User className="w-4 h-4" />
              Contractor *
            </Label>
            <Select 
              value={formData.client_id} 
              onValueChange={handleContractorSelect}
            >
              <SelectTrigger className="h-14 bg-[#1E2530] border-white/20 text-white text-base">
                <SelectValue placeholder="Select contractor" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10 max-h-60">
                {contractors.length === 0 ? (
                  <div className="p-3 text-gray-400 text-sm">No contractors found</div>
                ) : (
                  contractors.map(contractor => (
                    <SelectItem 
                      key={contractor.id} 
                      value={contractor.id}
                      className="text-white focus:bg-[#F5A623]/20 py-3"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{contractor.name}</span>
                        {contractor.company && (
                          <span className="text-xs text-gray-400">{contractor.company}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Site Address */}
          <div className="space-y-2">
            <Label className="text-gray-300 text-base flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Site Address *
            </Label>
            <Textarea
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              placeholder="Enter the site address"
              rows={2}
              className="bg-[#1E2530] border-white/20 text-white resize-none text-base"
            />
          </div>

          {/* Postcode */}
          <div className="space-y-2">
            <Label className="text-gray-300 text-base">Postcode</Label>
            <Input
              value={formData.postcode}
              onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value.toUpperCase() }))}
              placeholder="e.g., SW1A 1AA"
              className="h-14 bg-[#1E2530] border-white/20 text-white text-base uppercase"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-gray-300 text-base flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Description
            </Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of the project"
              rows={3}
              className="bg-[#1E2530] border-white/20 text-white resize-none text-base"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-gray-300 text-base">Additional Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional notes..."
              rows={2}
              className="bg-[#1E2530] border-white/20 text-white resize-none text-base"
            />
          </div>

          {/* Submit Button */}
          <Button 
            onClick={handleSubmit}
            disabled={loading}
            className="w-full h-14 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] text-base font-semibold active:scale-[0.98] transition-transform"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <FolderPlus className="w-5 h-5 mr-2" />
            )}
            Create Project
          </Button>
          
          <p className="text-gray-500 text-xs text-center">
            Project will be created as LUX Construction (CON). Admins can add further details on desktop.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
